import { useEffect, useState } from "react";
import { WITH_COOKIE } from "../tokenStorage";
import { AdminNav } from "./AdminPage";

/** A sent (or failed) one-off email, as app/routers/admin.py logs it. */
interface SentEmail {
  id: string;
  to: string;
  subject: string;
  attachment_names: string;
  created_at: string;
  status: "sent" | "failed";
  error: string | null;
}

interface AdminEmailPageProps {
  apiUrl: string;
  token: string;
  tokenResolved: boolean;
}

async function failure(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Not JSON: fall through to the status.
  }
  return `HTTP ${res.status}`;
}

/**
 * A one-off email to a customer, from the support address, with whatever
 * attachments it needs. Kept to the admin the same way /admin is: the API
 * answers 404 to anyone else.
 */
export function AdminEmailPage({ apiUrl, token, tokenResolved }: AdminEmailPageProps) {
  const [access, setAccess] = useState<"checking" | "denied" | "granted">("checking");
  const [sent, setSent] = useState<SentEmail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);

  const post = (path: string) =>
    fetch(`${apiUrl}${path}`, {
      ...WITH_COOKIE,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token || null }),
    });

  const loadSent = async () => {
    const res = await post("/api/admin/emails/list");
    if (res.ok) setSent(await res.json());
  };

  useEffect(() => {
    if (!tokenResolved) return;
    (async () => {
      try {
        const res = await post("/api/admin/me");
        if (!res.ok) {
          setAccess("denied");
          return;
        }
        setAccess("granted");
        await loadSent();
      } catch (err) {
        setError(`Could not reach the server: ${err}`);
        setAccess("denied");
      }
    })();
  }, [tokenResolved, token]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const form = new FormData();
      if (token) form.append("token", token);
      form.append("to", to);
      form.append("subject", subject);
      form.append("body", body);
      for (const file of files) form.append("files", file);
      const res = await fetch(`${apiUrl}/api/admin/emails`, {
        ...WITH_COOKIE,
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        // Kept as typed, so a failed send can be retried as is.
        setError(await failure(res));
        await loadSent();
        return;
      }
      setNotice(`Sent to ${to}.`);
      setTo("");
      setSubject("");
      setBody("");
      setFiles([]);
      setFileInputKey((k) => k + 1);
      await loadSent();
    } catch (err) {
      setError(`Could not send: ${err}`);
    } finally {
      setBusy(false);
    }
  };

  if (access === "checking") return <p aria-busy="true">Loading…</p>;

  if (access === "denied") {
    return (
      <article>
        <h2>Admin</h2>
        <p>This page isn't available.</p>
      </article>
    );
  }

  return (
    <>
      <AdminNav current="email" />
      <article>
        <h2>Send an email</h2>
        <form onSubmit={send}>
          <label>
            To
            <input type="email" required value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label>
            Subject
            <input type="text" required value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label>
            Message
            <textarea rows={10} required value={body} onChange={(e) => setBody(e.target.value)} />
            <small>Plain text, sent from support@embroiderytext.com. Replies come back there.</small>
          </label>
          <label>
            Attachments (optional)
            <input
              key={fileInputKey}
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
          </label>
          <button type="submit" disabled={busy} aria-busy={busy}>
            Send
          </button>
        </form>
        {error && <p style={{ color: "var(--pico-del-color, #b3261e)" }}>{error}</p>}
        {notice && <p>{notice}</p>}
      </article>

      <article>
        <h2>Sent</h2>
        {sent === null && <p aria-busy="true">Loading…</p>}
        {sent?.length === 0 && <p>Nothing sent yet.</p>}
        {sent && sent.length > 0 && (
          <div className="overflow-auto">
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">To</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {sent.map((email) => (
                  <tr key={email.id}>
                    <td>{new Date(email.created_at).toLocaleString()}</td>
                    <td>{email.to}</td>
                    <td>
                      {email.subject}
                      {email.attachment_names && (
                        <>
                          <br />
                          <small>{email.attachment_names}</small>
                        </>
                      )}
                    </td>
                    <td>
                      {email.status === "sent" ? "Sent" : "Failed"}
                      {email.error && (
                        <>
                          <br />
                          <small>{email.error}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </>
  );
}
